import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getBlockPosition,
  getTotalIntakeSteps,
} from "@/lib/stage1Questionnaire";

export type TransitionPhase = "entering" | "idle" | "exiting";
export type TransitionDirection = "forward" | "backward";

const STEP_PATH_PREFIX = "/intake/step/";

function getIntakeFlowStep(pathname: string): number {
  const total = getTotalIntakeSteps();
  if (pathname.startsWith(STEP_PATH_PREFIX)) {
    const position = getBlockPosition(pathname.slice(STEP_PATH_PREFIX.length));
    return position >= 0 ? position + 2 : 0;
  }
  const staticFlow: Record<string, number> = {
    "/intake": 1,
    "/intake/klevel-blueprint": total - 3,
    "/intake/account": total - 2,
    "/intake/myfit-scan": total - 1,
    "/intake/verification": total,
  };
  return staticFlow[pathname] ?? 0;
}

export function usePageTransition() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<TransitionPhase>("entering");

  useEffect(() => {
    setPhase("entering");
    const raf = requestAnimationFrame(() => {
      setPhase("idle");
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  // Re-read on every render (not memoized) so a route change via useLocation always
  // picks up the direction goTo() just wrote to sessionStorage.
  const direction: TransitionDirection =
    (sessionStorage.getItem(
      "intake_transition_direction",
    ) as TransitionDirection) || "forward";

  const goTo = useCallback(
    (path: string) => {
      const currentStep = getIntakeFlowStep(location.pathname);
      const targetStep = getIntakeFlowStep(path);
      const dir: TransitionDirection =
        targetStep >= currentStep ? "forward" : "backward";
      sessionStorage.setItem("intake_transition_direction", dir);
      setPhase("exiting");
      setTimeout(() => {
        navigate(path);
      }, 380);
    },
    [navigate, location.pathname],
  );

  const containerClasses = useMemo(() => {
    if (phase === "entering") {
      return direction === "forward"
        ? "opacity-0 translate-x-6 translate-y-2 blur-[2px]"
        : "opacity-0 -translate-x-6 translate-y-2 blur-[2px]";
    }
    if (phase === "exiting") {
      return direction === "forward"
        ? "opacity-0 -translate-x-4 -translate-y-1 blur-[1px] transition-all duration-300 ease-in"
        : "opacity-0 translate-x-4 -translate-y-1 blur-[1px] transition-all duration-300 ease-in";
    }
    return "opacity-100 translate-x-0 translate-y-0 blur-0";
  }, [phase, direction]);

  return { phase, direction, goTo, containerClasses };
}
